import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Trash2, UserPlus, Shield, X, Check, Edit } from 'lucide-react';

interface AdminUser {
    id: string;
    email: string;
    permissions: string[];
    created_at: any;
}

const AVAILABLE_PERMISSIONS = [
    { id: 'manage_users', label: 'Manage Team & Users' },
    { id: 'manage_finance', label: 'Manage Finance & Invoices' },
    { id: 'view_analytics', label: 'View Analytics' },
    { id: 'manage_settings', label: 'Manage Settings' },
];

const DEFAULT_ADMIN_PERMISSIONS = AVAILABLE_PERMISSIONS.map(({ id }) => id);

export default function TeamManagement() {
    const { user } = useAuth();
    const [admins, setAdmins] = useState<AdminUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [invitePermissions, setInvitePermissions] = useState<string[]>(DEFAULT_ADMIN_PERMISSIONS);
    const [sendingInvite, setSendingInvite] = useState(false);

    // Edit state
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedUserForEdit, setSelectedUserForEdit] = useState<AdminUser | null>(null);
    const [editPermissions, setEditPermissions] = useState<string[]>([]);
    const [updatingPermissions, setUpdatingPermissions] = useState(false);

    const fetchAdmins = async () => {
        setLoading(true);
        try {
            const manageTeam = httpsCallable(functions, 'manageTeam');
            const result = await manageTeam({ action: 'list' });
            const data = result.data as { users: AdminUser[] };
            setAdmins(data.users);
        } catch (err: any) {
            console.error('Error fetching admins:', err);
            setError('Failed to load team members');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAdmins();
    }, []);

    const handleInvite = async (e: React.FormEvent) => {
        e.preventDefault();
        setSendingInvite(true);
        setError(null);
        if (invitePermissions.length === 0) {
            setError('Select at least one permission for the admin user.');
            setSendingInvite(false);
            return;
        }
        try {
            const manageTeam = httpsCallable(functions, 'manageTeam');
            const result = await manageTeam({
                action: 'invite',
                user: {
                    email: inviteEmail,
                    permissions: invitePermissions
                }
            });

            const data = result.data as any;
            if (data.inviteLink) {
                // Show invite link to admin
                // In a real app, maybe a specialized modal, but alert/prompt works for MVP
                prompt('User invited successfully! Copy this link and send it to them:', data.inviteLink);
            } else {
                alert('User invited successfully!');
            }

            setShowInviteModal(false);
            setInviteEmail('');
            setInvitePermissions(DEFAULT_ADMIN_PERMISSIONS);
            fetchAdmins();
        } catch (err: any) {
            console.error('Invite error:', err);
            setError(err.message || 'Failed to invite user');
        } finally {
            setSendingInvite(false);
        }
    };

    const handleUpdatePermissions = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedUserForEdit) return;

        setUpdatingPermissions(true);
        setError(null);
        if (editPermissions.length === 0) {
            setError('Admin users must keep at least one permission.');
            setUpdatingPermissions(false);
            return;
        }
        try {
            const manageTeam = httpsCallable(functions, 'manageTeam');
            await manageTeam({
                action: 'update_permissions',
                user: {
                    id: selectedUserForEdit.id,
                    permissions: editPermissions
                }
            });

            alert('Permissions updated successfully!');
            setShowEditModal(false);
            setSelectedUserForEdit(null);
            setEditPermissions([]);
            fetchAdmins();
        } catch (err: any) {
            console.error('Update error:', err);
            setError(err.message || 'Failed to update permissions');
        } finally {
            setUpdatingPermissions(false);
        }
    };

    const toggleEditPermission = (permId: string) => {
        setEditPermissions(prev =>
            prev.includes(permId)
                ? prev.filter(p => p !== permId)
                : [...prev, permId]
        );
    };

    const openEditModal = (adminUser: AdminUser) => {
        setSelectedUserForEdit(adminUser);
        setEditPermissions(adminUser.permissions || []);
        setShowEditModal(true);
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to remove this admin?')) return;
        try {
            const manageTeam = httpsCallable(functions, 'manageTeam');
            await manageTeam({ action: 'delete', user: { id } });
            fetchAdmins();
        } catch (err: any) {
            console.error('Delete error:', err);
            setError(err.message || 'Failed to remove user');
        }
    };

    const togglePermission = (permId: string) => {
        setInvitePermissions(prev =>
            prev.includes(permId)
                ? prev.filter(p => p !== permId)
                : [...prev, permId]
        );
    };

    if (loading && admins.length === 0) return <div className="p-8 text-center text-gray-500">Loading team...</div>;

    return (
        <div className="p-8 max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-linear-to-r from-blue-600 to-indigo-600">
                        Team Management
                    </h1>
                    <p className="text-gray-500 mt-1">Manage admin access and permissions</p>
                </div>
                <button
                    onClick={() => setShowInviteModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-linear-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 shadow-md transition-all hover:scale-[1.02]"
                >
                    <UserPlus size={18} />
                    Invite Member
                </button>
            </div>

            {error && (
                <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg border border-red-100 flex items-center gap-2">
                    <Shield size={18} />
                    {error}
                </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                            <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                            <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Permissions</th>
                            <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {admins.map((adminUser) => (
                            <tr key={adminUser.id} className="hover:bg-gray-50 transition-colors">
                                <td className="py-4 px-6">
                                    <div className="font-medium text-gray-900">{adminUser.email}</div>
                                    <div className="text-xs text-gray-400 mt-0.5">ID: {adminUser.id.substring(0, 8)}...</div>
                                </td>
                                <td className="py-4 px-6">
                                    <div className="flex flex-wrap gap-2">
                                        {adminUser.permissions?.length > 0 ? (
                                            adminUser.permissions.map(perm => (
                                                <span key={perm} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-100">
                                                    {perm.replace('manage_', '').replace('view_', '').replace('_', ' ')}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="text-xs text-gray-400 italic">No permissions assigned</span>
                                        )}
                                    </div>
                                </td>
                                <td className="py-4 px-6 text-right">
                                    <div className="flex justify-end gap-2">
                                        <button
                                            onClick={() => openEditModal(adminUser)}
                                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                            title="Edit Permissions"
                                        >
                                            <Edit size={18} />
                                        </button>
                                        {adminUser.id !== user?.uid && (
                                            <button
                                                onClick={() => handleDelete(adminUser.id)}
                                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Remove User"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Invite Modal */}
            {showInviteModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <h3 className="font-semibold text-gray-900">Invite Team Member</h3>
                            <button onClick={() => setShowInviteModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleInvite} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                                <input
                                    type="email"
                                    required
                                    value={inviteEmail}
                                    onChange={e => setInviteEmail(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                                    placeholder="colleague@example.com"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
                                <div className="space-y-2">
                                    {AVAILABLE_PERMISSIONS.map(perm => (
                                        <label key={perm.id} className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${invitePermissions.includes(perm.id) ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 bg-white'}`}>
                                                {invitePermissions.includes(perm.id) && <Check size={12} strokeWidth={3} />}
                                            </div>
                                            <input
                                                type="checkbox"
                                                className="hidden"
                                                checked={invitePermissions.includes(perm.id)}
                                                onChange={() => togglePermission(perm.id)}
                                            />
                                            <span className="text-sm text-gray-600">{perm.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="pt-4 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowInviteModal(false)}
                                    className="flex-1 px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={sendingInvite}
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                    {sendingInvite ? 'Inviting...' : 'Send Invite'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedUserForEdit && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <h3 className="font-semibold text-gray-900">Edit Permissions</h3>
                            <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleUpdatePermissions} className="p-6 space-y-4">
                            <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
                                <p className="text-sm font-medium text-blue-900">{selectedUserForEdit.email}</p>
                                <p className="text-xs text-blue-700">Update access levels for this team member</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
                                <div className="space-y-2">
                                    {AVAILABLE_PERMISSIONS.map(perm => (
                                        <label key={perm.id} className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${editPermissions.includes(perm.id) ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 bg-white'}`}>
                                                {editPermissions.includes(perm.id) && <Check size={12} strokeWidth={3} />}
                                            </div>
                                            <input
                                                type="checkbox"
                                                className="hidden"
                                                checked={editPermissions.includes(perm.id)}
                                                onChange={() => toggleEditPermission(perm.id)}
                                            />
                                            <span className="text-sm text-gray-600">{perm.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="pt-4 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowEditModal(false)}
                                    className="flex-1 px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={updatingPermissions}
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                    {updatingPermissions ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
